import React, { useEffect, useState, useRef, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useUserService } from '@hooks/services/useUserService';
import { useLogger } from '@providers/loggerProvider';
import { components } from '@theme/classNames';
import {
    subscribeToBricklinkQuota,
    getBricklinkQuota,
    seedBricklinkQuotaFromUser
} from '@utils/bricklinkQuotaStore';

const { pill, label, muted, usage, thresholds, quotaBadge } = components.apiCounter;

const LIMIT_TOOLTIP = [
    'BrickLink allows up to 5,000 API calls per day.',
    'This is a soft limit. BrickBuddy keeps working past it and does not disable any feature.',
    'Past the limit BrickLink itself may start rejecting calls, so these can fail:',
    '• Inventory sync (push to BrickLink store)',
    '• Order import from BrickLink',
    '• Catalog item lookups (parts, sets, prices)',
    '• Category data refresh',
    '• Superset / subset lookups',
    'The counter resets on a rolling 24-hour window, not at midnight.'
].join('\n');

const getUsageClass = (percentage) =>
    usage[thresholds.find((t) => percentage >= t.minPercent).usageKey];

const BrickLinkApiCounter = () => {
    const { getUser } = useUserService();
    const logger = useLogger('BrickLinkApiCounter');
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // The API reports usage and any overage on every response. The store is the
    // single source for all three numbers so the badge can never contradict the
    // count beside it — the mount fetch below seeds it, responses refresh it.
    const quota = useSyncExternalStore(subscribeToBricklinkQuota, getBricklinkQuota);

    // Use refs to avoid dependency issues in useEffect
    const getUserRef = useRef(getUser);
    const loggerRef = useRef(logger);

    // Keep refs updated
    useEffect(() => {
        getUserRef.current = getUser;
        loggerRef.current = logger;
    }, [getUser, logger]);

    // Fetch user data on component mount and periodically
    useEffect(() => {
        const fetchUserData = async (forceRefresh) => {
            try {
                loggerRef.current.debug('Fetching user data for BrickLink counter');
                const user = await getUserRef.current(forceRefresh);

                if (user) {
                    setUserData(user);
                    // A user without BrickLink keys has no quota to show; the render
                    // below validates the flag and links to Settings instead.
                    if (user.bricklink_connected === true) {
                        seedBricklinkQuotaFromUser({
                            current_count: user.bricklink_daily_api_query_count || 0,
                            daily_limit: user.bricklink_daily_api_limit || 0
                        });
                    }
                    loggerRef.current.debug('User data fetched', {
                        dailyLimit: user.bricklink_daily_api_limit,
                        currentCount: user.bricklink_daily_api_query_count
                    });
                }
                setIsLoading(false);
            } catch (error) {
                loggerRef.current.error('Failed to fetch user data', error);
                setIsLoading(false);
            }
        };

        fetchUserData(false);

        const interval = setInterval(() => fetchUserData(true), 300000);

        return () => clearInterval(interval);
    }, []); // Empty deps - only run on mount
    
    if (isLoading) {
        return (
            <div className={pill} title={LIMIT_TOOLTIP}>
                <span className={label}>BrickLink API Calls:</span>
                <span className={muted}>Loading...</span>
            </div>
        );
    }

    if (!userData) {
        return (
            <div className={pill} title={LIMIT_TOOLTIP}>
                <span className={label}>BrickLink API Calls:</span>
                <span className={muted}>Unavailable</span>
            </div>
        );
    }

    if (typeof userData.bricklink_connected !== 'boolean') {
        throw new Error('BrickLinkApiCounter: bricklink_connected must be a boolean');
    }

    if (!userData.bricklink_connected) {
        return (
            <div className={pill} title={LIMIT_TOOLTIP}>
                <span className={label}>BrickLink:</span>
                <Link to="/app/settings" className={muted} data-testid="bricklink-not-connected">
                    Not connected
                </Link>
            </div>
        );
    }

    const currentCount = quota === null ? 0 : quota.current_count;
    const dailyLimit = quota === null ? 0 : quota.daily_limit;
    const quotaWarning = quota === null ? null : quota.warning;

    const percentage = dailyLimit > 0 ? (currentCount / dailyLimit) * 100 : 0;

    return (
        <div className={pill} title={quotaWarning === null ? LIMIT_TOOLTIP : `${quotaWarning.message}\n\n${LIMIT_TOOLTIP}`}>
            <span className={label}>BrickLink API Calls:</span>
            <span className={`font-bold ${getUsageClass(percentage)}`}>
                {currentCount}
            </span>
            <span className={muted}>/</span>
            <span className={label}>{dailyLimit}</span>
            {quotaWarning !== null && (
                <span className={quotaBadge} data-testid="bricklink-quota-warning">
                    Over quota
                </span>
            )}
            {percentage >= 75 && (
                <span className={usage.percentage}>
                    ({Math.round(percentage)}%)
                </span>
            )}
        </div>
    );
};

export default BrickLinkApiCounter;
